import { useState } from 'react';

import { api, ApiError } from '../../api';
import { Session } from '../../auth';
import { Panel, Segmented } from '../../ui';

// The internal ISO-operator console (DECISION-059) — the operator/billing console
// the roadmap spine flagged, here as its first surface: a form that mints a hotel
// or supplier tenant + its first sign-in identity through POST /v1/accounts (the
// one provisioning primitive). Its own minimal shell — no hotel/supplier rail —
// because an operator is a third principal that reaches no tenant screen. NOT one
// of the 41 registered marketplace screens (a register decision, recorded).

type AccType = 'hotel' | 'supplier';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function AdminConsole({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const [type, setType] = useState<AccType>('hotel');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('Europe/Athens');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ accountId: string; accountType: string; created: boolean } | null>(null);

  const identityOk = type === 'hotel' ? EMAIL_RE.test(email.trim()) : /^\+\d{8,15}$/.test(phone.replace(/\s/g, ''));
  const canSubmit = !!name.trim() && identityOk && !busy;

  const submit = async () => {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const body: Record<string, string> = { accountType: type, name: name.trim(), timezone: timezone.trim() };
      if (type === 'hotel') body.email = email.trim();
      else body.phone = phone.replace(/\s/g, '');
      const r = await api<{ accountId: string; accountType: string; created: boolean }>('POST', '/accounts', body);
      setResult(r);
      setName('');
      setEmail('');
      setPhone('');
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shell">
      <nav className="topnav">
        <span className="brandmark">I</span>
        <span className="crumb">
          <span className="here">ISO operator · new account</span>
        </span>
        <span className="right">
          <span className="property-pill">Operator</span>
          <span className="avatar">{session.name.slice(0, 1).toUpperCase()}</span>
          <button className="signout" onClick={onSignOut}>
            Sign out
          </button>
        </span>
      </nav>

      <main className="content" style={{ maxWidth: 560, margin: '0 auto', padding: 'var(--space-lg)' }}>
        <h1>Create an account</h1>
        <p className="welcome">
          Mint a hotel or supplier tenant and its first sign-in identity — the same primitive the
          invitation flow uses, and the one self-signup will call.
        </p>

        <div className="field">
          <label>Account type</label>
          <Segmented
            value={type}
            onChange={setType}
            options={[
              { value: 'hotel', label: 'Hotel' },
              { value: 'supplier', label: 'Supplier' },
            ]}
          />
        </div>

        <div className="field">
          <label htmlFor="acc-name">Business name</label>
          <input id="acc-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </div>

        {type === 'hotel' ? (
          <div className="field">
            <label htmlFor="acc-email">Sign-in email</label>
            <input id="acc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <p className="hint" style={{ marginTop: 6 }}>
              The hotel signs in with a magic link to this address — no password to hand over.
            </p>
          </div>
        ) : (
          <div className="field">
            <label htmlFor="acc-phone">Sign-in phone</label>
            <input id="acc-phone" className="mono" placeholder="+30 691 234 5678" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <p className="hint" style={{ marginTop: 6 }}>
              The supplier signs in with a 6-digit SMS code — the phone is their identifier, no password.
            </p>
          </div>
        )}

        <div className="field">
          <label htmlFor="acc-tz">Timezone</label>
          <input id="acc-tz" className="mono" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
        </div>

        {error && (
          <p className="error-line" role="alert">
            {error}
          </p>
        )}

        <button className="btn lg" disabled={!canSubmit} onClick={submit} style={{ marginTop: 8 }}>
          {busy ? 'Creating…' : 'Create account'}
        </button>

        {result && (
          <div style={{ marginTop: 'var(--space-lg)' }}>
            <Panel>
              <strong>{result.created ? 'Account created.' : 'That identity already had an account.'}</strong>
              <div className="mono" style={{ marginTop: 6 }}>
                accountId {result.accountId}
              </div>
              <div className="hint" style={{ marginTop: 4 }}>
                {result.accountType} · give them this id and their {result.accountType === 'hotel' ? 'email' : 'phone'} to sign in.
              </div>
            </Panel>
          </div>
        )}
      </main>
    </div>
  );
}
